/* Talking to the gateway.
 *
 * One client for all 836 DocTypes, matching the seven routes the gateway
 * exposes. It is deliberately not a data-fetching framework: the engine needs
 * request/abort/retry and an error shape it can put on a field, and nothing
 * else here has to be clever.
 */
import * as queue from './queue'
import { mayQueue } from './queue'
import type { DocFlow, FlowAction, Payment, StepTaken } from './money/types'
import type {
  ApiError, AutomationRules, BomCost, BomExploded, BomNode, BomUse,
  ContactAbout, ContactList, CustomiseSurface, EmailThread, ErrorFix, FoundDoc, GstMonth,
  ModuleState, Notice, Overview,
  Person, RoleReach,
  Doc, DocMeta, ImportPreview, ImportResult, ListAnswer, NumberCard,
  ReportAnswer, ReportDefinition, ReportRow, WebsiteAnswer,
} from './types'
import type {
  InstallReport, MakeWorkBody, PackListing, PacksActive, StepResult, WorkDetail, WorkList, WorkPage,
  InvoiceDraft, PackPeople, ReleaseState, TeamsConnection, WorkPerson, WorkReport, WorkReportListing,
  DepartmentPlacing, DepartmentTree, ProjectBody, WorkProjectDetail, WorkProjects, WorkScope,
  RunningTimer, TimeMode, TimerResult,
} from './work/types'

export type ExportKind = 'csv' | 'xlsx' | 'pdf'

export type ListQuery = {
  fields?: string[]
  filters?: unknown[]
  orFilters?: unknown[]
  orderBy?: string
  start?: number
  limit?: number
  withCount?: boolean
}

export class ErpError extends Error {
  readonly code: string
  readonly fields: Record<string, string>
  readonly retryable: boolean
  readonly requestId: string
  readonly status: number
  /** Somewhere to go and put it right, when the refusal named a setting. */
  readonly fix?: ErrorFix

  constructor(e: ApiError) {
    super(e.message)
    this.name = 'ErpError'
    this.code = e.code
    this.fields = e.fields ?? {}
    this.retryable = e.retryable
    this.requestId = e.request_id
    this.status = e.status
    this.fix = e.fix
  }

  /** A 409 carries the other person's values for the fields you changed. */
  get conflictedFields() { return this.code === 'conflict' ? this.fields : {} }
}

export type ErpClientOptions = {
  base: string
  /** Extra headers — in development, the identity switcher's. */
  headers?: () => Record<string, string>
  /**
   * The session has ended as far as the gateway is concerned — the token
   * expired, or was revoked somewhere else.
   *
   * It is a callback rather than something this module does itself, because
   * what "signed out" *means* belongs to the app and its router, not to the
   * thing that makes HTTP calls. Without it an expired token shows "You do
   * not have permission" on every screen at once, which reads as the product
   * breaking rather than as a session ending.
   */
  onUnauthorized?: () => void
}

/** Was this cancelled, rather than failed?
 *
 *  Checked two ways because browsers disagree: the signal itself is the
 *  reliable one, the error name is the fallback. */
function isAbort(error: unknown, signal?: AbortSignal | null): boolean {
  if (signal?.aborted) return true
  const name = (error as { name?: string } | null)?.name
  return name === 'AbortError'
}


/** What a held write was, in words a person recognises. */
function describe(method: string, path: string): string {
  const parts = path.split('/').filter(Boolean)
  const at = parts.indexOf('doc')
  const doctype = at >= 0 ? decodeURIComponent(parts[at + 1] ?? '') : ''
  const name = at >= 0 ? decodeURIComponent(parts[at + 2] ?? '') : ''
  if (!doctype) return 'A change'
  return method === 'POST' && !name ? `A new ${doctype}` : `A change to ${name || doctype}`
}


export class ErpClient {
  constructor(private readonly options: ErpClientOptions) {}

  private async send<T>(path: string, init: RequestInit = {}): Promise<T> {
    const method = (init.method ?? 'GET').toUpperCase()
    let response: Response
    try {
      response = await this.attempt(path, init)
    } catch (networkError) {
      // An abort is not a connection problem. Every screen cancels its
      // in-flight requests when it unmounts or when the filters change, and
      // React runs effects twice in development -- so treating a throw as
      // "offline" turned ordinary cancellation into "there is no connection"
      // on the home tiles, the dashboard and the reports list.
      if (isAbort(networkError, init.signal)) throw networkError

      // A read that could not go out is just a read that could not go out.
      // Nothing is held and nothing is lost, so it must not borrow the
      // sentence about the books.
      if (method === 'GET') {
        throw new ErpError({
          code: 'offline', retryable: true, fields: {}, status: 0, request_id: '',
          message: 'There is no connection, so this could not be loaded. It will work '
                 + 'again as soon as there is one.',
        })
      }

      // A write. An ordinary edit is held and sent when the signal comes
      // back; anything that moves a document through its lifecycle is
      // refused, because replaying a submit against a document somebody else
      // has moved on is how a ledger gets hurt.
      if (mayQueue(method, path)) {
        const held = queue.add({
          method: method as 'POST' | 'PUT', path,
          body: String(init.body ?? ''),
          what: describe(method, path),
        })
        throw new ErpError({
          code: 'held_until_online', retryable: true, fields: {}, status: 0,
          request_id: held.id,
          message: 'There is no connection, so this is saved on the device and will go '
                 + 'out when there is one.',
        })
      }
      throw new ErpError({
        code: 'offline', retryable: true, fields: {}, status: 0, request_id: '',
        message: 'There is no connection. This one has to wait until there is, because '
               + 'it changes the books.',
      })
    }
    return this.readAnswer<T>(response)
  }

  /** Send whatever is waiting. Returns how many went. */
  async flushOutbox(): Promise<{ sent: number; left: number; failed?: string }> {
    return queue.flush(async (held) => {
      const response = await this.attempt(held.path, {
        method: held.method,
        body: held.body,
        headers: { 'Content-Type': 'application/json' },
      })
      if (!response.ok && response.status !== 207) {
        // A refusal is not a connection problem: it will never succeed, so
        // it is dropped rather than retried forever. The person is told.
        if (response.status >= 400 && response.status < 500) return
        throw new Error(`${response.status}`)
      }
    })
  }

  /** What is still waiting to go out. */
  outbox() { return queue.read() }

  /** The request itself. Throws only on a *connection* failure, which is how
   *  `send` tells "there is no network" apart from "the server said no". */
  private async attempt(path: string, init: RequestInit = {}): Promise<Response> {
    return fetch(`${this.options.base}${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(this.options.headers?.() ?? {}),
        ...(init.headers as Record<string, string> | undefined),
      },
    })
  }

  private async readAnswer<T>(response: Response): Promise<T> {
    if (response.status === 204) return undefined as T
    const text = await response.text()
    const body = text ? safeJson(text) : null
    if (!response.ok) {
      const shaped = (body as { error?: ApiError } | null)?.error
      if (response.status === 401) this.options.onUnauthorized?.()
      throw new ErpError({
        code: shaped?.code ?? 'internal_error',
        message: shaped?.message ?? 'Something went wrong.',
        fields: shaped?.fields ?? {},
        retryable: shaped?.retryable ?? response.status >= 500,
        request_id: shaped?.request_id ?? '',
        status: response.status,
        fix: shaped?.fix,
      })
    }
    return body as T
  }

  meta(doctype: string, opts: { refresh?: boolean } = {}) {
    return this.send<DocMeta>(`/v1/meta/${encodeURIComponent(doctype)}/${opts.refresh ? '?refresh=1' : ''}`)
  }

  forgetMeta(doctype: string) {
    return this.send<{ forgotten: string }>(`/v1/meta/${encodeURIComponent(doctype)}/forget/`, { method: 'POST', body: '{}' })
  }

  list(doctype: string, q: ListQuery = {}, signal?: AbortSignal) {
    const params = new URLSearchParams()
    if (q.fields?.length) params.set('fields', JSON.stringify(q.fields))
    if (q.filters?.length) params.set('filters', JSON.stringify(q.filters))
    if (q.orFilters?.length) params.set('or_filters', JSON.stringify(q.orFilters))
    if (q.orderBy) params.set('order_by', q.orderBy)
    if (q.start) params.set('start', String(q.start))
    if (q.limit) params.set('limit', String(q.limit))
    if (q.withCount) params.set('with_count', '1')
    return this.send<ListAnswer>(`/v1/doc/${encodeURIComponent(doctype)}/?${params}`, { signal })
  }

  /** The current list as a file: the same filters, sort and columns. */
  downloadList(doctype: string, q: ListQuery, kind: 'csv' | 'xlsx') {
    const params = new URLSearchParams({ export: kind })
    if (q.fields?.length) params.set('fields', JSON.stringify(q.fields))
    if (q.filters?.length) params.set('filters', JSON.stringify(q.filters))
    if (q.orderBy) params.set('order_by', q.orderBy)
    return this.saveAs(`${this.options.base}/v1/export/${encodeURIComponent(doctype)}/?${params}`,
                       `${doctype}.${kind}`, 'That list could not be downloaded.')
  }

  /** One action, many documents. Answers 207 when some worked and some did
   *  not, with the refusals named — partial success is the normal case. */
  bulk(doctype: string, action: 'submit' | 'cancel' | 'delete' | 'assign' | 'edit',
       names: string[], extra: Record<string, unknown> = {}) {
    return this.send<{ action: string; done: string[]; total: number; succeeded: number
                       failed: { name: string; why: string; code: string }[] }>(
      `/v1/bulk/${encodeURIComponent(doctype)}/`, {
        method: 'POST',
        body: JSON.stringify({ action, names, ...extra }),
        headers: { 'Idempotency-Key': newIdempotencyKey() },
      })
  }

  groupCounts(doctype: string, field: string, filters?: unknown[], signal?: AbortSignal) {
    const params = new URLSearchParams({ field })
    if (filters?.length) params.set('filters', JSON.stringify(filters))
    return this.send<{ counts: { name: string; count: number }[] }>(
      `/v1/group-count/${encodeURIComponent(doctype)}/?${params}`, { signal })
  }

  /** The document and everything hanging off it, in one request. */
  read(doctype: string, name: string, signal?: AbortSignal) {
    return this.send<{ doc: Doc; docinfo: Record<string, unknown> }>(
      `/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/`, { signal })
  }

  create(doctype: string, values: Doc, idempotencyKey?: string) {
    return this.send<Doc>(`/v1/doc/${encodeURIComponent(doctype)}/`, {
      method: 'POST', body: JSON.stringify(values),
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    })
  }

  /** Only what changed, plus the `modified` the person was looking at, so a
   *  simultaneous edit is a 409 naming the fields rather than a silent
   *  overwrite. */
  update(doctype: string, name: string, changes: Doc, seenModified?: string, idempotencyKey?: string) {
    return this.send<Doc>(`/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/`, {
      method: 'PUT',
      body: JSON.stringify({ ...changes, __seen_modified: seenModified }),
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    })
  }

  remove(doctype: string, name: string) {
    return this.send<void>(`/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/`, { method: 'DELETE' })
  }

  action(doctype: string, name: string, verb: 'submit' | 'cancel', idempotencyKey?: string) {
    return this.send<Doc>(`/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/${verb}/`, {
      method: 'POST', body: '{}',
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    })
  }

  /** The steps this document's workflow allows. An empty list means the
   *  DocType has no workflow, which is the ordinary case. */
  transitions(doctype: string, name: string, signal?: AbortSignal) {
    return this.send<{ transitions: { action?: string; next_state?: string }[]; state_field?: string | null }>(
      `/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/workflow/`, { signal })
  }

  applyWorkflow(doctype: string, name: string, action: string) {
    return this.send<Doc>(`/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/workflow/`, {
      method: 'POST', body: JSON.stringify({ action }),
      headers: { 'Idempotency-Key': newIdempotencyKey() },
    })
  }

  /** The values for a second document like this one — not saved. Many
   *  DocTypes derive their name from their fields, so a straight server-side
   *  copy collides with the original. */
  duplicate(doctype: string, name: string) {
    return this.send<{ doctype: string; copied_from: string; values: Doc }>(
      `/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/duplicate/`)
  }

  renameDoc(doctype: string, name: string, newName: string) {
    return this.send<{ name: string }>(`/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/rename/`, {
      method: 'POST', body: JSON.stringify({ new_name: newName }),
    })
  }

  shareWith(doctype: string, name: string, user: string, write = false) {
    return this.send<{ user: string }>(`/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/share/`, {
      method: 'POST', body: JSON.stringify({ user, write }),
    })
  }

  unshare(doctype: string, name: string, user: string) {
    return this.send<void>(
      `/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/unshare/?user=${encodeURIComponent(user)}`,
      { method: 'DELETE' })
  }

  sharedWith(doctype: string, name: string, signal?: AbortSignal) {
    return this.send<{ shared: { user: string; read?: number; write?: number }[] }>(
      `/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/shared/`, { signal })
  }

  /** Who a comment can mention, or a document can be shared with. */
  people(text = '', signal?: AbortSignal) {
    return this.send<{ people: { name: string; full_name?: string }[] }>(
      `/v1/people/?q=${encodeURIComponent(text)}`, { signal })
  }

  /** A list's metadata and its first page, in one round trip.
   *
   * Opening a list needed the metadata before it knew which columns to ask
   * for, so it was two serial requests behind the bundle. On a mid-range
   * Android on 4G that measured 1883ms against a 1200ms budget, nearly all of
   * it waiting. Everything after the first paint — paging, sorting, filtering
   * — still uses `list`, because those really do not need the metadata again.
   */
  listScreen(doctype: string,
             options: { fields?: string[]; extra?: string[]; limit?: number } = {}) {
    const params = new URLSearchParams({ with_count: '1' })
    if (options.fields?.length) params.set('fields', JSON.stringify(options.fields))
    // Fields a view needs that are not columns. The server adds the map's own
    // by reading the metadata; this is for the ones only the client knows.
    if (options.extra?.length) params.set('extra', JSON.stringify(options.extra))
    if (options.limit) params.set('limit', String(options.limit))
    return this.send<{ meta: DocMeta; list: ListAnswer }>(
      `/v1/screen/list/${encodeURIComponent(doctype)}/?${params}`)
  }

  /** Everything an auditor asks for, as one file. */
  downloadAuditPack(company: string, fromDate: string, toDate: string) {
    const params = new URLSearchParams({ company, from_date: fromDate, to_date: toDate })
    return this.saveAs(`${this.options.base}/v1/audit-pack/?${params}`,
                       `audit pack ${company} ${fromDate} to ${toDate}.zip`,
                       'The audit pack could not be built.')
  }

  /** Everyone who can reach the ERP here. Not `people`, which is the much
   *  smaller "who could I assign this document to" list. */
  everyoneWithAccess() {
    return this.send<{ site: string; people: Person[] }>('/v1/admin/people/')
  }

  setAccess(email: string, allowed: boolean) {
    return this.send<{ email: string }>(
      `/v1/admin/people/${encodeURIComponent(email)}/access/`,
      { method: 'POST', body: JSON.stringify({ allowed }) })
  }

  personRoles(email: string) {
    return this.send<{ email: string; roles: string[]; from_their_jackdesk_role: string[] }>(
      `/v1/admin/people/${encodeURIComponent(email)}/roles/`)
  }

  setPersonRoles(email: string, roles: string[]) {
    return this.send<{ roles: string[] }>(
      `/v1/admin/people/${encodeURIComponent(email)}/roles/`,
      { method: 'POST', body: JSON.stringify({ roles }) })
  }

  rolesOnOffer() {
    return this.send<{ roles: { name: string; ours: boolean }[] }>('/v1/admin/roles/')
  }

  whatARoleReaches(role: string) {
    return this.send<RoleReach>(`/v1/admin/roles/${encodeURIComponent(role)}/reach/`)
  }

  /** Where Frappe and Jackdesk disagree about who somebody is. */
  drift() { return this.send<Record<string, unknown>>('/v1/admin/drift/') }

  /** A month's GST, from the books. */
  gstMonth(company: string, fromDate: string, toDate: string) {
    const params = new URLSearchParams({ company, from_date: fromDate, to_date: toDate })
    return this.send<GstMonth>(`/v1/gst/?${params}`)
  }

  downloadGstPack(company: string, fromDate: string, toDate: string) {
    const params = new URLSearchParams({ company, from_date: fromDate, to_date: toDate })
    return this.saveAs(`${this.options.base}/v1/gst/pack/?${params}`,
                       `GST ${company} ${fromDate} to ${toDate}.zip`,
                       'The GST working papers could not be built.')
  }

  /** What this organisation has, and whether each is paid for. */
  modules() { return this.send<ModuleState>('/v1/modules/') }

  /** The email about this document. */
  emails(doctype: string, name: string) {
    return this.send<EmailThread>(
      `/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/emails/`)
  }

  sendEmail(doctype: string, name: string, mail: Record<string, unknown>) {
    return this.send<{ sent: boolean }>(
      `/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/emails/send/`,
      { method: 'POST', body: JSON.stringify(mail) })
  }

  /** Documents matching, across the whole ERP. */
  find(q: string) {
    return this.send<{ q: string; hits: FoundDoc[] }>(`/v1/find/?q=${encodeURIComponent(q)}`)
  }

  /** What has happened that concerns me. */
  notifications() {
    return this.send<{ unread: number; notifications: Notice[] }>('/v1/notifications/')
  }

  markNoticesRead(ids?: string[]) {
    return this.send<{ marked: number }>('/v1/notifications/read/',
      { method: 'POST', body: JSON.stringify(ids ? { ids } : { all: true }) })
  }

  /** Everyone and every company, once each, with their roles. */
  contacts(q = '') {
    return this.send<ContactList>(`/v1/contacts/?q=${encodeURIComponent(q)}`)
  }

  contactAbout(doctype: string, name: string) {
    return this.send<ContactAbout>(`/v1/contacts/about/?doctype=${encodeURIComponent(doctype)}` +
      `&name=${encodeURIComponent(name)}`)
  }

  /** Approvals, alerts and rules set up for this form. */
  automationFor(doctype: string) {
    return this.send<AutomationRules>(`/v1/automation/${encodeURIComponent(doctype)}/`)
  }

  buildWorkflow(doctype: string, spec: Record<string, unknown>) {
    return this.send<{ workflow: string }>(
      `/v1/automation/${encodeURIComponent(doctype)}/workflow/`,
      { method: 'POST', body: JSON.stringify(spec) })
  }

  switchWorkflow(doctype: string, name: string, active: boolean) {
    return this.send<{ active: boolean }>(
      `/v1/automation/${encodeURIComponent(doctype)}/workflow/${encodeURIComponent(name)}/`,
      { method: 'POST', body: JSON.stringify({ active }) })
  }

  removeWorkflow(doctype: string, name: string) {
    return this.send<void>(
      `/v1/automation/${encodeURIComponent(doctype)}/workflow/${encodeURIComponent(name)}/remove/`,
      { method: 'DELETE' })
  }

  /** What is on this form, and what an admin may change about it. */
  customiseSurface(doctype: string) {
    return this.send<CustomiseSurface>(`/v1/customise/${encodeURIComponent(doctype)}/`)
  }

  addCustomField(doctype: string, field: Record<string, unknown>) {
    return this.send<{ field: Doc }>(`/v1/customise/${encodeURIComponent(doctype)}/fields/`,
      { method: 'POST', body: JSON.stringify(field) })
  }

  removeCustomField(doctype: string, name: string) {
    return this.send<void>(
      `/v1/customise/${encodeURIComponent(doctype)}/fields/${encodeURIComponent(name)}/`,
      { method: 'DELETE' })
  }

  changeField(doctype: string, change: Record<string, unknown>) {
    return this.send<{ change: string }>(`/v1/customise/${encodeURIComponent(doctype)}/changes/`,
      { method: 'POST', body: JSON.stringify(change) })
  }

  undoChange(doctype: string, name: string) {
    return this.send<void>(
      `/v1/customise/${encodeURIComponent(doctype)}/changes/${encodeURIComponent(name)}/`,
      { method: 'DELETE' })
  }

  setNumbering(doctype: string, series: string) {
    return this.send<{ series: string }>(
      `/v1/customise/${encodeURIComponent(doctype)}/numbering/`,
      { method: 'POST', body: JSON.stringify({ series }) })
  }

  /** One level of a bill of materials. */
  bomTree(name: string) {
    return this.send<{ parent: string; children: BomNode[] }>(
      `/v1/bom/${encodeURIComponent(name)}/tree/`)
  }

  /** Every raw material at the bottom of it, as ERPNext already worked out. */
  bomExploded(name: string) {
    return this.send<BomExploded>(`/v1/bom/${encodeURIComponent(name)}/exploded/`)
  }

  /** What it costs, and what makes that up. */
  bomCost(name: string) {
    return this.send<BomCost>(`/v1/bom/${encodeURIComponent(name)}/cost/`)
  }

  /** What else this part goes into. */
  bomWhereUsed(item: string) {
    return this.send<{ item: string; used_in: BomUse[] }>(
      `/v1/bom/where-used/?item=${encodeURIComponent(item)}`)
  }

  /** What is in the spreadsheet, and which column we think is which field. */
  importPreview(doctype: string, file: File) {
    const body = new FormData()
    body.append('file', file)
    return this.sendForm<ImportPreview>(`/v1/import/${encodeURIComponent(doctype)}/preview/`, body)
  }

  /** Try every row, or actually do it. */
  importRun(doctype: string, file: File, mapping: Record<string, string | null>, dryRun: boolean) {
    const body = new FormData()
    body.append('file', file)
    body.append('mapping', JSON.stringify(mapping))
    body.append('dry_run', dryRun ? '1' : '0')
    return this.sendForm<ImportResult>(`/v1/import/${encodeURIComponent(doctype)}/run/`, body)
  }

  private async sendForm<T>(path: string, body: FormData): Promise<T> {
    const response = await fetch(`${this.options.base}${path}`, {
      method: 'POST', body,
      headers: { Accept: 'application/json', ...(this.options.headers?.() ?? {}) },
    })
    const text = await response.text()
    const parsed = text ? safeJson(text) : null
    // 207 means some rows worked and some did not — the normal case for an
    // import, and not an error.
    if (!response.ok && response.status !== 207) {
      const shaped = (parsed as { error?: ApiError } | null)?.error
      throw new ErpError({
        code: shaped?.code ?? 'internal_error',
        message: shaped?.message ?? 'That file could not be read.',
        fields: shaped?.fields ?? {}, retryable: false,
        request_id: shaped?.request_id ?? '', status: response.status,
      })
    }
    return parsed as T
  }

  /** What can be made next from this document. */
  nextSteps(doctype: string, name: string, signal?: AbortSignal) {
    return this.send<{ next: { target: string; label: string; ready: boolean }[]; why_not?: string | null }>(
      `/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/next/`, { signal })
  }

  /** Build the next document as an unsaved draft. */
  makeNext(doctype: string, name: string, target: string) {
    return this.send<{ doctype: string; from: { doctype: string; name: string }; values: Doc }>(
      `/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/make/`, {
        method: 'POST', body: JSON.stringify({ target }),
      })
  }

  /** A fresh draft of a cancelled document, pointing back at it. */
  amend(doctype: string, name: string) {
    return this.send<Doc>(`/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/amend/`, {
      method: 'POST', body: '{}',
      headers: { 'Idempotency-Key': newIdempotencyKey() },
    })
  }

  addComment(doctype: string, name: string, content: string) {
    return this.send<Doc>(`/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/comment/`, {
      method: 'POST', body: JSON.stringify({ content }),
      headers: { 'Idempotency-Key': newIdempotencyKey() },
    })
  }

  assign(doctype: string, name: string, people: string[], description?: string) {
    return this.send<Doc>(`/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/assign/`, {
      method: 'POST', body: JSON.stringify({ assign_to: people, description }),
    })
  }

  unassign(doctype: string, name: string, user: string) {
    return this.send<void>(
      `/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/assign/?user=${encodeURIComponent(user)}`,
      { method: 'DELETE' })
  }

  addTag(doctype: string, name: string, tag: string) {
    return this.send<{ tag: string }>(`/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/tag/`, {
      method: 'POST', body: JSON.stringify({ tag }),
    })
  }

  removeTag(doctype: string, name: string, tag: string) {
    return this.send<{ tag: string }>(
      `/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/tag/?tag=${encodeURIComponent(tag)}`,
      { method: 'DELETE' })
  }

  /** What else in the system points at this document. */
  documentLinks(doctype: string, name: string, signal?: AbortSignal) {
    return this.send<{ links: { doctype: string; names: string[]; count: number }[] }>(
      `/v1/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/links/`, { signal })
  }

  /** One level of a hierarchy. No parent means the roots. */
  treeChildren(doctype: string, parent?: string, signal?: AbortSignal) {
    const params = new URLSearchParams()
    if (parent) params.set('parent', parent)
    return this.send<{
      parent_field: string; title_field?: string; children: Doc[]; truncated: boolean
      /** Chart of Accounts only: each account's balance, rolled up the tree
       *  exactly as ERPNext's own desk rolls it. */
      balances?: Record<string, number | null>
      currency?: string
    }>(`/v1/tree/${encodeURIComponent(doctype)}/?${params}`, { signal })
  }

  /** The tenant's public website: what is published, and at what address. */
  website(signal?: AbortSignal) {
    return this.send<WebsiteAnswer>('/v1/website/', { signal })
  }

  /** The business over a fiscal year: the figures an owner opens the app for,
   *  each one traceable back to the rows it was summed from. */
  overview(opts: { fiscalYear?: string; company?: string } = {}, signal?: AbortSignal) {
    const params = new URLSearchParams()
    if (opts.fiscalYear) params.set('fiscal_year', opts.fiscalYear)
    if (opts.company) params.set('company', opts.company)
    return this.send<Overview>(`/v1/overview/?${params}`, { signal })
  }

  /** Number cards with their values, in one request rather than one per
   *  tile: twelve round trips from a phone is most of the budget before
   *  anything is drawn. */
  numberCards(opts: { module?: string; names?: string[] } = {}, signal?: AbortSignal) {
    const params = new URLSearchParams()
    if (opts.module) params.set('module', opts.module)
    if (opts.names?.length) params.set('names', opts.names.join(','))
    return this.send<{ cards: NumberCard[] }>(`/v1/cards/?${params}`, { signal })
  }

  chartCatalogue(module?: string, signal?: AbortSignal) {
    const params = new URLSearchParams()
    if (module) params.set('module', module)
    return this.send<{ charts: { name: string; chart_name?: string; type?: string; module?: string; chart_type?: string }[] }>(
      `/v1/charts/?${params}`, { signal })
  }

  /** One chart's data. A chart that cannot be drawn answers `ok: false` with
   *  a reason rather than a 500 — one bad tile must not blank a dashboard. */
  chartData(name: string, signal?: AbortSignal) {
    return this.send<{ name: string; ok: boolean; why?: string; labels?: string[]
                       datasets?: { name?: string; values?: (number | null)[] }[]; type?: string }>(
      `/v1/charts/${encodeURIComponent(name)}/`, { signal })
  }

  reports(doctype?: string, signal?: AbortSignal) {
    const params = new URLSearchParams()
    if (doctype) params.set('doctype', doctype)
    return this.send<{ reports: ReportRow[] }>(`/v1/reports/?${params}`, { signal })
  }

  reportDefinition(name: string, signal?: AbortSignal) {
    return this.send<ReportDefinition>(`/v1/reports/${encodeURIComponent(name)}/`, { signal })
  }

  runReport(name: string, filters: Record<string, unknown>, signal?: AbortSignal) {
    return this.send<ReportAnswer>(`/v1/reports/${encodeURIComponent(name)}/run/`, {
      method: 'POST', body: JSON.stringify({ filters }), signal,
    })
  }

  /** `export`, never `format` — that one is reserved further down the stack
   *  and silently swallowed. */
  reportExportUrl(name: string, filters: Record<string, unknown>, kind: ExportKind) {
    const params = new URLSearchParams({ filters: JSON.stringify(filters), export: kind })
    return `${this.options.base}/v1/reports/${encodeURIComponent(name)}/export/?${params}`
  }

  /** Download the report as a file.
   *
   *  Fetched, not linked. An <a href> cannot carry the headers that say who
   *  is asking, so a download link answered 403 while the same report
   *  displayed on screen perfectly well — which looks like a broken
   *  download rather than a missing credential. */
  downloadReport(name: string, filters: Record<string, unknown>, kind: ExportKind) {
    return this.saveAs(this.reportExportUrl(name, filters, kind), `${name}.${kind}`,
                       'That report could not be downloaded.')
  }

  printFormats(doctype: string, name: string, signal?: AbortSignal) {
    return this.send<{ formats: { name: string }[]; default: string; letterheads: { name: string }[] }>(
      `/v1/print/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/formats/`, { signal })
  }

  printUrl(doctype: string, name: string, printFormat?: string) {
    const params = new URLSearchParams()
    if (printFormat) params.set('print_format', printFormat)
    return `${this.options.base}/v1/print/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/pdf/?${params}`
  }

  /** Fetch the PDF and hand it to the browser as a download.
   *
   *  Not a plain <a href>: the request needs the same headers as every other
   *  call, and an anchor cannot carry them. A failure this way is an error
   *  the screen can show, rather than a tab containing an error page. */
  /** The printed document as HTML, to look at before committing to paper. */
  printPreview(doctype: string, name: string, printFormat?: string, signal?: AbortSignal) {
    const params = new URLSearchParams()
    if (printFormat) params.set('print_format', printFormat)
    return this.send<{ html: string; style: string }>(
      `/v1/print/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/preview/?${params}`, { signal })
  }

  /** Several documents, one PDF. */
  async downloadManyPrints(doctype: string, names: string[], printFormat?: string): Promise<void> {
    const response = await fetch(`${this.options.base}/v1/print-many/${encodeURIComponent(doctype)}/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(this.options.headers?.() ?? {}) },
      body: JSON.stringify({ names, print_format: printFormat }),
    })
    if (!response.ok) {
      const shaped = (safeJson(await response.text()) as { error?: ApiError } | null)?.error
      throw new ErpError({
        code: shaped?.code ?? 'internal_error',
        message: shaped?.message ?? 'Those could not be printed together.',
        fields: {}, retryable: false, request_id: shaped?.request_id ?? '', status: response.status,
      })
    }
    const href = URL.createObjectURL(await response.blob())
    const link = document.createElement('a')
    link.href = href; link.download = `${doctype} x${names.length}.pdf`
    document.body.appendChild(link); link.click(); link.remove()
    window.setTimeout(() => URL.revokeObjectURL(href), 10_000)
  }

  emailDocument(doctype: string, name: string, body: {
    recipients: string; subject?: string; content?: string; print_format?: string; copy_me?: boolean
  }) {
    return this.send<Doc>(`/v1/print/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/email/`, {
      method: 'POST', body: JSON.stringify(body),
    })
  }

  downloadPrint(doctype: string, name: string, printFormat?: string) {
    return this.saveAs(this.printUrl(doctype, name, printFormat),
                       `${doctype} ${name}.pdf`, 'That could not be printed.')
  }

  /** Fetch a file with our headers and hand it to the browser. */
  private async saveAs(url: string, filename: string, whenItFails: string): Promise<void> {
    const response = await fetch(url, { headers: { ...(this.options.headers?.() ?? {}) } })
    if (!response.ok) {
      const shaped = (safeJson(await response.text()) as { error?: ApiError } | null)?.error
      throw new ErpError({
        code: shaped?.code ?? 'internal_error',
        message: shaped?.message ?? whenItFails,
        fields: {}, retryable: false, request_id: shaped?.request_id ?? '',
        status: response.status,
      })
    }
    const href = URL.createObjectURL(await response.blob())
    const link = document.createElement('a')
    link.href = href
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    // Freed later: revoking it immediately races the download in Safari and
    // the file arrives empty.
    window.setTimeout(() => URL.revokeObjectURL(href), 10_000)
  }

  /** Attach a file to a saved document. Multipart, so no Content-Type is set
   *  here -- the browser has to add its own boundary. */
  async upload(body: FormData): Promise<{ file_url?: string; file_name?: string; name?: string }> {
    const response = await fetch(`${this.options.base}/v1/upload/`, {
      method: 'POST', body,
      headers: { Accept: 'application/json', ...(this.options.headers?.() ?? {}) },
    })
    const text = await response.text()
    const parsed = text ? safeJson(text) : null
    if (!response.ok) {
      const shaped = (parsed as { error?: ApiError } | null)?.error
      throw new ErpError({
        code: shaped?.code ?? 'internal_error',
        message: shaped?.message ?? 'That file could not be attached.',
        fields: shaped?.fields ?? {}, retryable: shaped?.retryable ?? false,
        request_id: shaped?.request_id ?? '', status: response.status,
      })
    }
    return (parsed ?? {}) as { file_url?: string }
  }

  /* ── industry packs and the work they add ─────────────────────────── */

  /** Every pack, and whether this organisation has it. */
  packs(signal?: AbortSignal) {
    return this.send<{ packs: PackListing[]; may_manage: boolean }>('/v1/packs/', { signal })
  }

  /** What the screens need from the packs switched on here. */
  packsActive(signal?: AbortSignal) {
    return this.send<PacksActive>('/v1/packs/active/', { signal })
  }

  /** Install or upgrade. `check` says what would change and writes nothing. */
  installPack(id: string, check: boolean) {
    return this.send<InstallReport>(`/v1/packs/${encodeURIComponent(id)}/install/`, {
      method: 'POST', body: JSON.stringify({ check }),
    })
  }

  /** The owner's choices for a pack: its deadlines in the Calendar, how time is recorded. */
  packSettings(id: string, body: { calendar?: boolean; time?: TimeMode }) {
    return this.send<{ pack: string; calendar: boolean; time: TimeMode }>(`/v1/packs/${encodeURIComponent(id)}/settings/`, {
      method: 'POST', body: JSON.stringify(body),
    })
  }

  /** Off hides it and deletes nothing. */
  switchPack(id: string, enabled: boolean) {
    return this.send<{ pack: string; enabled: boolean }>(`/v1/packs/${encodeURIComponent(id)}/switch/`, {
      method: 'POST', body: JSON.stringify({ enabled }),
    })
  }

  workMine(scope: WorkScope, closed: boolean, signal?: AbortSignal) {
    const params = new URLSearchParams({ scope })
    if (closed) params.set('closed', '1')
    return this.send<WorkList>(`/v1/work/mine/?${params}`, { signal })
  }

  /** A page of the list, sorted and searched by the gateway (the table, when the list is long). */
  workPage(ask: { scope: WorkScope; closed: boolean; sort: string; dir: 'asc' | 'desc'; start: number;
                  limit: number; q?: string; overdue?: boolean }, signal?: AbortSignal) {
    const params = new URLSearchParams({ scope: ask.scope, sort: ask.sort, dir: ask.dir,
                                         start: String(ask.start), limit: String(ask.limit) })
    if (ask.closed) params.set('closed', '1')
    if (ask.overdue) params.set('overdue', '1')
    if (ask.q?.trim()) params.set('q', ask.q.trim())
    return this.send<WorkPage>(`/v1/work/mine/?${params}`, { signal })
  }

  workDetail(name: string, signal?: AbortSignal) {
    return this.send<WorkDetail>(`/v1/work/${encodeURIComponent(name)}/`, { signal })
  }

  /** People who hold a role (or any of several), for a step's picker. */
  workPeople(role?: string | string[], signal?: AbortSignal) {
    const roles = (Array.isArray(role) ? role : role ? [role] : []).filter((r) => r && r !== 'everyone')
    const params = roles.length ? `?role=${encodeURIComponent(roles.join(','))}` : ''
    return this.send<{ people: WorkPerson[]; role: string | null }>(`/v1/work/people/${params}`, { signal })
  }

  /** Move a piece of work's start and/or deadline (YYYY-MM-DD; null clears). A job's open rows
   *  move with it unless `rows` is false. */
  moveWork(name: string, body: { exp_start_date?: string | null; exp_end_date?: string | null; rows?: boolean }) {
    return this.send<{ name: string; exp_start_date?: string | null; exp_end_date?: string | null
                       moved_rows: number; note_not_saved?: string
                       job_moved?: string; project_moved?: Record<string, string> }>(
      `/v1/work/${encodeURIComponent(name)}/dates/`, { method: 'POST', body: JSON.stringify(body) })
  }

  /** Change who does a piece of work, checks, reviews or approves it: only what is sent changes. */
  changeWorkPeople(name: string, body: { makers?: string[]; checker?: string; reviewer?: string; approver?: string }) {
    return this.send<{ name: string; changed: string[]; note_not_saved?: string }>(
      `/v1/work/${encodeURIComponent(name)}/people/`, { method: 'POST', body: JSON.stringify(body) })
  }

  /** Start or stop my timer on a piece of work (starting stops any other of mine). */
  workTimer(name: string, action: 'start' | 'stop') {
    return this.send<TimerResult>(`/v1/work/${encodeURIComponent(name)}/timer/`, {
      method: 'POST', body: JSON.stringify({ action }),
    })
  }

  /** My running timer, wherever it is. */
  myTimer(signal?: AbortSignal) {
    return this.send<{ running: RunningTimer | null }>('/v1/work/timer/', { signal })
  }

  /** A department's people, from the organisation's own directory. */
  departmentPeople(department: string, signal?: AbortSignal) {
    return this.send<{ people: WorkPerson[]; department: string; problem?: string | null }>(
      `/v1/work/people/?department=${encodeURIComponent(department)}`, { signal })
  }

  /** ERP departments with the organisation's own placed under them, for a picker. */
  workDepartments(signal?: AbortSignal) {
    return this.send<DepartmentTree>('/v1/work/departments/', { signal })
  }

  /** Both department lists and how they are joined (owner). */
  departmentPlacing(signal?: AbortSignal) {
    return this.send<DepartmentPlacing>('/v1/packs/departments/', { signal })
  }

  /** Put departments under ERP ones; a null parent takes one out. */
  placeDepartments(links: { jackdesk: number; parent: string | null }[]) {
    return this.send<DepartmentPlacing>('/v1/packs/departments/', {
      method: 'POST', body: JSON.stringify({ links }),
    })
  }

  workProjects(options: { scope?: 'all' | 'managed'; closed?: boolean } = {}, signal?: AbortSignal) {
    const params = new URLSearchParams()
    if (options.scope === 'managed') params.set('scope', 'managed')
    if (options.closed) params.set('closed', '1')
    const q = params.toString()
    return this.send<WorkProjects>(`/v1/work/projects/${q ? `?${q}` : ''}`, { signal })
  }

  workProject(name: string, signal?: AbortSignal) {
    return this.send<WorkProjectDetail>(`/v1/work/projects/${encodeURIComponent(name)}/`, { signal })
  }

  makeProject(body: ProjectBody) {
    return this.send<{ name: string; project_name: string }>('/v1/work/projects/', {
      method: 'POST', body: JSON.stringify(body),
    })
  }

  updateProject(name: string, body: ProjectBody) {
    return this.send<WorkProjectDetail>(`/v1/work/projects/${encodeURIComponent(name)}/`, {
      method: 'PATCH', body: JSON.stringify(body),
    })
  }

  /** A new client by name, from the project form. */
  makeClient(customerName: string) {
    return this.send<{ name: string; customer_name: string }>('/v1/work/clients/', {
      method: 'POST', body: JSON.stringify({ customer_name: customerName }),
    })
  }

  makeWork(body: MakeWorkBody, idempotencyKey: string) {
    return this.send<{ name: string; rows: string[] }>('/v1/work/', {
      method: 'POST', body: JSON.stringify(body),
      headers: { 'Idempotency-Key': idempotencyKey },
    })
  }

  /** One step, with the reply that goes with it, in one save. */
  workStep(name: string, body: { action: string; values?: Record<string, unknown>
                                  hours?: number; reason?: string; note?: string },
           idempotencyKey: string) {
    return this.send<StepResult>(`/v1/work/${encodeURIComponent(name)}/step/`, {
      method: 'POST', body: JSON.stringify(body),
      headers: { 'Idempotency-Key': idempotencyKey },
    })
  }

  /** Everyone at the organisation and the pack roles each holds. */
  packPeople(signal?: AbortSignal) {
    return this.send<PackPeople>('/v1/packs/people/', { signal })
  }

  /** Exactly these pack roles for one person; their other roles stay. */
  setPackRoles(email: string, roles: string[]) {
    return this.send<{ email: string; roles: string[]; created: boolean }>('/v1/packs/people/', {
      method: 'POST', body: JSON.stringify({ email, roles }),
    })
  }

  /** The organisation's Teams app, for cards when work moves. */
  teams(signal?: AbortSignal) {
    return this.send<TeamsConnection>('/v1/packs/teams/', { signal })
  }

  connectTeams(baseUrl: string, botToken: string) {
    return this.send<TeamsConnection>('/v1/packs/teams/', {
      method: 'POST', body: JSON.stringify({ base_url: baseUrl, bot_token: botToken }),
    })
  }

  disconnectTeams() {
    return this.send<TeamsConnection>('/v1/packs/teams/', { method: 'DELETE' })
  }

  /** Where the cards for this work's project go; '' stops them. */
  setWorkChannel(name: string, channel: string) {
    return this.send<{ project: string; channel: string | null }>(
      `/v1/work/${encodeURIComponent(name)}/channel/`, { method: 'POST', body: JSON.stringify({ channel }) })
  }

  /** What a job's draft invoice would say, line by line. */
  workInvoice(name: string, signal?: AbortSignal) {
    return this.send<InvoiceDraft>(`/v1/work/${encodeURIComponent(name)}/invoice/`, { signal })
  }

  /** Raise the draft Sales Invoice for a job's approved, unbilled rows. */
  /** A sale or a purchase step by step: its chain, stage and the one next step (PK-65). */
  docFlow(doctype: string, name: string, signal?: AbortSignal) {
    return this.send<DocFlow>(`/v1/flow/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/`, { signal })
  }

  /** Take the step on offer: finalise, make the next document, record a payment, a return. */
  takeStep(doctype: string, name: string, step: { action: FlowAction; target?: string | null; payment?: Payment
                                                   /** Why a document is cancelled and re-issued: kept with both. */
                                                   reason?: string },
           idempotencyKey: string) {
    return this.send<StepTaken>(`/v1/flow/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}/`, {
      method: 'POST', body: JSON.stringify(step), headers: { 'Idempotency-Key': idempotencyKey },
    })
  }

  raiseWorkInvoice(name: string, idempotencyKey: string) {
    return this.send<{ invoice: string; total: number; lines: number; rows: string[] }>(
      `/v1/work/${encodeURIComponent(name)}/invoice/`, {
        method: 'POST', body: '{}', headers: { 'Idempotency-Key': idempotencyKey },
      })
  }

  /** Releasing approved work to the client: who, which files, which revision. */
  workRelease(name: string, signal?: AbortSignal) {
    return this.send<ReleaseState>(`/v1/work/${encodeURIComponent(name)}/release/`, { signal })
  }

  releaseWork(name: string, body: { to: string[]; cc?: string[]; note?: string; files?: string[] },
              idempotencyKey: string) {
    return this.send<{ sent: boolean; rev: number; to: string[]; files: string[] }>(
      `/v1/work/${encodeURIComponent(name)}/release/`, {
        method: 'POST', body: JSON.stringify(body), headers: { 'Idempotency-Key': idempotencyKey },
      })
  }

  workReports(signal?: AbortSignal) {
    return this.send<{ reports: WorkReportListing[] }>('/v1/work/reports/', { signal })
  }

  /** One pack report for a period; every figure names the tasks behind it. */
  workReport(key: string, from?: string, to?: string, signal?: AbortSignal) {
    const params = new URLSearchParams()
    if (from) params.set('from', from)
    if (to) params.set('to', to)
    return this.send<WorkReport>(`/v1/work/reports/${encodeURIComponent(key)}/?${params}`, { signal })
  }

  searchLink(doctype: string, text: string, filters?: Record<string, unknown>, signal?: AbortSignal) {
    const params = new URLSearchParams({ doctype, q: text })
    if (filters && Object.keys(filters).length) params.set('filters', JSON.stringify(filters))
    return this.send<{ results: { value: string; label?: string; description?: string }[] }>(
      `/v1/search/?${params}`, { signal })
  }
}

function safeJson(text: string): unknown {
  try { return JSON.parse(text) } catch { return { error: { message: text.slice(0, 200) } } }
}

/** A key that makes a retry a replay rather than a second document. */
export const newIdempotencyKey = () =>
  `erp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
